<template>
  <div :class="['side-menu-wrapper', { collapsed: listCollapsed }]" ref="sideMenuWrapperRef">
    <div
      :class="['side-menu-list', 'thin-scroll', { collapsed: listCollapsed }]"
      ref="sideMenuListRef"
      @keydown.up.prevent.stop="focusPreviousMenuItem()"
      @keydown.down.prevent.stop="focusNextMenuItem()"
    >
      <div v-for="(entry, index) in entries" :key="index" class="side-menu-entry-wrapper">
        <template v-if="entry.children?.length">
          <div
            :class="['side-menu-entry', { collapsed: listCollapsed }]"
            tabindex="0"
            data-qa="side-menu-entry"
            @click="entryClick($event, index as number)"
            @keypress.space.prevent
            @keyup.space.stop="entryClick($event, index as number)"
            @keydown.enter.stop="entryClick($event, index as number)"
          >
            <template v-if="listCollapsed && isEntryContainingCurrentPage(entry)">
              <div class="side-menu-button-marker" />
              <div class="side-menu-button-shade" />
            </template>
            <component
              v-if="entry.icon"
              :is="entry.icon"
              class="side-menu-icon"
              data-qa="side-menu-icon"
            />
            <span :class="['side-menu-entry-title', { collapsed: listCollapsed }]">
              {{ t(`sideMenu.${entry.translationKey}`) }}
            </span>
            <icon-chevron
              v-if="!listCollapsed"
              color="stratos"
              :direction="expandedEntryIndexes.includes(index) ? 'up' : 'down'"
            />
          </div>
          <div v-if="!listCollapsed && expandedEntryIndexes.includes(index)">
            <template v-for="(listItem, index) in entry.children" :key="index">
              <template v-if="!listItem.children?.length">
                <router-link
                  :class="['side-menu-list-item', { selected: isCurrentPage(listItem) }]"
                  tabindex="0"
                  data-qa="side-menu-list-item"
                  :to="getPath(listItem.link)"
                  @click="setListCollapsed(true)"
                  @keypress.space.prevent
                  @keyup.space.stop="navigateToPage(listItem)"
                  @keydown.enter.stop="navigateToPage(listItem)"
                >
                  <div class="side-menu-list-item-marker" />
                  <div class="side-menu-list-item-title">
                    {{ t(`sideMenu.${listItem.translationKey}`) }}
                  </div>
                </router-link>
              </template>
              <template v-else>
                <div class="side-menu-list-item-subtitle">
                  {{ t(`sideMenu.${listItem.translationKey}`) }}
                </div>
                <template v-for="(sublistItem, index) in listItem.children" :key="index">
                  <router-link
                    :class="['side-menu-list-item', { selected: isCurrentPage(sublistItem) }]"
                    tabindex="0"
                    data-qa="side-menu-list-item"
                    :to="getPath(sublistItem.link)"
                    @click="setListCollapsed(true)"
                    @keypress.space.prevent
                    @keyup.space.stop="navigateToPage(sublistItem)"
                    @keydown.enter.stop="navigateToPage(sublistItem)"
                  >
                    <div class="side-menu-list-item-marker" />
                    <div class="side-menu-list-item-title">
                      {{ t(`sideMenu.${sublistItem.translationKey}`) }}
                    </div>
                  </router-link>
                </template>
              </template>
            </template>
          </div>
          <layout-divider-line
            v-if="!listCollapsed && index < entries.length - 1"
            color="light-gray"
            :class="{ 'group-divider-margined': expandedEntryIndexes.includes(index) }"
          />
        </template>
      </div>
    </div>
    <div
      class="list-expander"
      data-qa="side-menu-list-expander"
      @click="setListCollapsed(!listCollapsed)"
    >
      <icon-chevron color="stratos" :direction="listCollapsed ? 'right' : 'left'" />
    </div>
    <div class="side-menu-footer" v-if="!listCollapsed">
      <div class="version">
        {{ t('sideMenu.copyright', { version: planetVersion }) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import { useSideMenuStore } from '@/store/SideMenuStore';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import _ from 'lodash';
import { useRouter, useRoute } from 'vue-router';
import IconChevron from 'ui-component-library/assets/icons/IconChevron.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { useI18n } from 'vue-i18n';
import { useContextStore } from '@/store/ContextStore';
import RequestsUtils from '@/helpers/RequestsUtils';

const { t } = useI18n();

type ListEntry = {
  translationKey: string;
  icon: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  children: ListItem[];
  displayMode?: 'spa-only' | 'mfe-only' | 'both'; // Control visibility based on runtime mode
};

type ListItem = {
  translationKey: string;
  link?: string;
  additionalLinks?: string[];
  access?: string[];
  children?: ListItem[];
  displayMode?: 'spa-only' | 'mfe-only' | 'both'; // Control visibility based on runtime mode
};

const branchesStore = useBranchesStore();
const userStore = useUserStore();
const planetStore = usePlanetStore();
const contextStore = useContextStore();
const sideMenuStore = useSideMenuStore();
const cdnProvidersCount = ref<number | null>(null);

const itemAccessible = (listItem: ListItem) => {
  if (listItem.translationKey === 'purgeCdnCache') {
    if (cdnProvidersCount.value === 0) {
      return false;
    }
  }
  return !listItem.access || userStore.checkAccessLevel(listItem.access);
};

const isItemVisibleInCurrentMode = (item: ListItem | ListEntry) => {
  if (!item.displayMode || item.displayMode === 'both') {
    return true; // Default behavior: show in all modes
  }

  const isMicroFrontend = contextStore.isMicroFrontend;

  if (item.displayMode === 'spa-only') {
    return !isMicroFrontend; // Show only when NOT in micro-frontend mode
  }

  if (item.displayMode === 'mfe-only') {
    return isMicroFrontend; // Show only when in micro-frontend mode
  }

  return true;
};

const loadCdnProviders = async () => {
  try {
    const response = await RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `purge-cdn-cache/providers`,
    });
    const providers = response?.data?.items || [];
    cdnProvidersCount.value = providers.length;
  } catch (error) {
    console.error('Error loading CDN providers:', error);
    cdnProvidersCount.value = 0;
  }
};

onMounted(async () => {
  await loadCdnProviders();
});

const sideMenuEntries: ListEntry[] = [
  {
    translationKey: 'analytics',
    icon: defineAsyncComponent(() => import(`@/assets/icons/side-menu-icons/IconAnalytics.vue`)),
    children: [
      {
        translationKey: 'dashboard',
        link: '/dashboard',
        displayMode: 'both',
      },
      {
        translationKey: 'eventsLog',
        link: '/events-log',
        displayMode: 'both',
      },
      {
        translationKey: 'aiManagement',
        link: '/ai-management',
      },
    ],
  },
  {
    translationKey: 'security',
    icon: defineAsyncComponent(() => import(`@/assets/icons/side-menu-icons/IconSecurity.vue`)),
    children: [
      {
        translationKey: 'globalFilters',
        link: '/global-filters/list',
        additionalLinks: ['/global-filters/config', '/global-filters/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'flowControlPolicies',
        link: '/flow-control-policies/list',
        additionalLinks: ['/flow-control-policies/config', '/flow-control-policies/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'securityPolicies',
        link: '/security-policies/list',
        additionalLinks: ['/security-policies/config', '/security-policies/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'rateLimitRules',
        link: '/rate-limit-rules/list',
        additionalLinks: ['/rate-limit-rules/config', '/rate-limit-rules/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'aclProfiles',
        link: '/acl-profiles/list',
        additionalLinks: ['/acl-profiles/config', '/acl-profiles/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'actions',
        link: '/actions/list',
        additionalLinks: ['/actions/config', '/actions/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'dynamicRules',
        link: '/dynamic-rules/list',
        additionalLinks: ['/dynamic-rules/config', '/dynamic-rules/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'quarantined',
        link: '/quarantined',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'contentFilter',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        children: [
          {
            translationKey: 'contentFilterProfiles',
            link: '/content-filter-profiles/list',
            additionalLinks: ['/content-filter-profiles/config', '/content-filter-profiles/new'],
          },
          {
            translationKey: 'contentFilterRules',
            link: '/content-filter-rules/list',
            additionalLinks: ['/content-filter-rules/config', '/content-filter-rules/new'],
          },
        ],
        displayMode: 'both',
      },
    ],
  },
  {
    translationKey: 'sites',
    icon: defineAsyncComponent(() => import(`@/assets/icons/side-menu-icons/IconSites.vue`)),
    children: [
      {
        translationKey: 'serverGroups',
        link: '/server-groups/list',
        additionalLinks: ['/server-groups/config', '/server-groups/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'proxyTemplates',
        link: '/proxy-templates/list',
        additionalLinks: ['/proxy-templates/config', '/proxy-templates/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'mobileApplicationGroups',
        link: '/mobile-application-groups/list',
        additionalLinks: ['/mobile-application-groups/config', '/mobile-application-groups/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'backendServices',
        link: '/backend-services/list',
        additionalLinks: ['/backend-services/config', '/backend-services/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'edgeFunctions',
        link: '/edge-functions/list',
        additionalLinks: ['/edge-functions/config', '/edge-functions/new'],
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'dnsRecords',
        link: '/dns-records',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'ssl',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        children: [
          {
            translationKey: 'sslLoadBalancers',
            link: '/ssl/load-balancers',
          },
          {
            translationKey: 'serverCertificates',
            link: '/ssl/certificates',
          },
        ],
        displayMode: 'both',
      },
    ],
  },
  {
    translationKey: 'system',
    icon: defineAsyncComponent(() => import(`@/assets/icons/side-menu-icons/IconSystem.vue`)),
    children: [
      {
        translationKey: 'interactiveChallenge',
        link: '/interactive-challenge',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'ssoConfiguration',
        link: '/sso-configuration',
        access: ['waap-manager', 'waap-system-admin'],
        displayMode: 'spa-only',
      },
      {
        translationKey: 'purgeCdnCache',
        link: '/purge-cdn-cache',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'usersManagement',
        link: '/users-management',
        access: ['waap-manager', 'waap-system-admin'],
        displayMode: 'spa-only',
      },
      {
        translationKey: 'securityAlerts',
        link: '/security-alerts',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'logExporters',
        link: '/log-exporters',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'versionControl',
        link: '/version-control',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'systemDB',
        link: '/system-db/list',
        additionalLinks: ['/system-db/config', '/system-db/new'],
        access: ['waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
      {
        translationKey: 'publishChanges',
        link: '/publish',
        access: ['waap-config-editor', 'waap-manager', 'waap-system-admin'],
        displayMode: 'both',
      },
    ],
  },
];

const entries = computed(() => {
  const listEntries = _.map(sideMenuEntries, (listEntry: ListEntry) => {
    const listItems: ListItem[] = _.map(listEntry.children, (listItem: ListItem) => {
      if (!listItem.children) {
        return listItem;
      }
      listItem.children = _.filter(listItem.children, (sublistItem: ListItem) => {
        return itemAccessible(sublistItem) && isItemVisibleInCurrentMode(sublistItem);
      });
      return listItem;
    });
    listEntry.children = _.filter(listItems, (listItem: ListItem) => {
      return (
        itemAccessible(listItem) &&
        isItemVisibleInCurrentMode(listItem) &&
        (listItem.children ? !!listItem.children?.length : true)
      );
    });
    return listEntry;
  });
  return _.filter(listEntries, (listEntry: ListEntry) => {
    return !!listEntry.children?.length && isItemVisibleInCurrentMode(listEntry);
  });
});

const planetVersion = computed(() => {
  return planetStore.version;
});
const listCollapsed = computed(() => sideMenuStore?.isCollapsed ?? true);
const entriesKeys = entries.value?.keys();
const expandedEntryIndexes = ref([...entriesKeys]);
const router = useRouter();
const route = useRoute();
const sideMenuWrapperRef = ref(null);
const sideMenuListRef = ref(null);

const selectedBranch = computed(() => {
  if (!branchesStore.selectedBranchId) {
    branchesStore.setSelectedBranch(branchesStore.defaultBranch);
  }
  return branchesStore.selectedBranchId;
});

const entryClick = (event: Event, index: number) => {
  if (listCollapsed.value) {
    setListCollapsed(false);
    expandedEntryIndexes.value = [];
  }
  if (expandedEntryIndexes.value.includes(index)) {
    _.remove(expandedEntryIndexes.value, (entryIndex) => {
      return entryIndex === index;
    });
  } else {
    expandedEntryIndexes.value.push(index);
  }
  if (event.pointerType === 'mouse') {
    event.currentTarget.blur();
  }
};

const getPath = (link: string) => {
  if (contextStore.isShell && contextStore.isMicroFrontend) {
    return `${contextStore.basePath}/${selectedBranch.value}${link}`;
  }
  return `/${selectedBranch.value}${link}`;
};

const navigateToPage = (listItem: ListItem) => {
  router.push(getPath(listItem.link));
  setListCollapsed(true);
};

const isCurrentPage = (listItem: ListItem) => {
  const links = [listItem.link];
  if (listItem.additionalLinks) {
    links.push(...listItem.additionalLinks);
  }
  return _.some(links, (link: string) => {
    return route.path?.startsWith(getPath(link));
  });
};

const isEntryContainingCurrentPage = (listEntry: ListEntry) => {
  return _.some(listEntry.children, (listItem) => {
    if (listItem.children?.length) {
      return _.some(listItem.children, (sublistItem) => {
        return isCurrentPage(sublistItem);
      });
    } else {
      return isCurrentPage(listItem);
    }
  });
};

const setListCollapsed = (newValue: boolean) => {
  sideMenuStore.setCollapsed(newValue);
  if (!newValue) {
    setTimeout(() => {
      document.addEventListener('click', closeMenuListener);
      document.addEventListener('keydown', closeMenuListener);
    }, 0);
  } else {
    document.removeEventListener('click', closeMenuListener);
    document.removeEventListener('keydown', closeMenuListener);
  }
};

const focusPreviousMenuItem = () => {
  let indexToFocus: number;
  const focusableElements = sideMenuListRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index: number) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index - 1;
    }
  });
  if (!_.isNil(indexToFocus) && indexToFocus >= 0) {
    focusableElements[indexToFocus].focus();
  }
};

const focusNextMenuItem = () => {
  let indexToFocus;
  const focusableElements = sideMenuListRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index + 1;
    }
  });
  if (_.isNil(indexToFocus)) {
    indexToFocus = 0;
  }
  if (!_.isNil(indexToFocus) && indexToFocus < focusableElements.length) {
    focusableElements[indexToFocus].focus();
  }
};

const closeMenuListener = (event: Event) => {
  const isMouseClick = (event as MouseEvent).pointerType === 'mouse';
  const isEscape = (event as KeyboardEvent).code === 'Escape';
  if ((isMouseClick && !sideMenuWrapperRef.value.contains(event.target)) || isEscape) {
    setListCollapsed(true);
  }
  if (isEscape) {
    event.stopPropagation();
  }
};
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$menu-width-closed: 90px;
$menu-width-opened: 240px;

.side-menu-wrapper {
  background-color: map.get(colors.$colors, 'alabaster');
  border-bottom-right-radius: 50px;
  border-top-right-radius: 50px;
  box-shadow: 0 2px 8px rgba(5, 0, 56, 0.16);
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  user-select: none;

  // transition: width 0.3s ease-in-out;
  width: $menu-width-opened;

  &.collapsed {
    width: $menu-width-closed;
  }
}

.list-expander {
  background-color: map.get(colors.$colors, 'alabaster');
  border-radius: 50%;
  box-shadow: -2px 8px 12px rgba(5, 0, 56, 0.3);
  cursor: pointer;
  height: 24px;
  position: absolute;
  right: -12px;
  top: 96px;
  width: 24px;
}

.side-menu-list {
  bottom: 110px;
  overflow-y: auto;
  position: absolute;
  top: 140px;
  width: 100%;

  &.collapsed {
    padding-top: 10px;
    top: 130px;
  }

  .side-menu-icon {
    height: 32px;
    width: 32px;
  }

  .side-menu-entry {
    align-items: center;
    color: map.get(colors.$colors, 'biscay');
    cursor: pointer;
    display: flex;
    flex-direction: row;
    gap: 12px;
    padding: 12px 16px 12px 12px;
    position: relative;
    width: 100%;

    &.collapsed {
      flex-direction: column;
      gap: 8px;
      height: 90px;
      padding: 16px 0 20px;
    }

    &.collapsed:focus:not(:active) {
      border: 2px solid map.get(colors.$colors, 'magic-mint');
      outline: 0;
      padding: 14px 0 18px;
    }

    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
    }

    &:active {
      background-color: map.get(colors.$colors, 'shamrock');
    }

    &:focus:not(:active) {
      border: 2px solid map.get(colors.$colors, 'magic-mint');
      outline: 0;
      padding: 10px 14px 10px 10px;
    }
  }

  .side-menu-button-marker {
    background-color: map.get(colors.$colors, 'shamrock');
    border-bottom-right-radius: 6px;
    border-top-right-radius: 6px;
    height: 32px;
    left: 0;
    position: absolute;
    top: 25px;
    width: 6px;
  }

  .side-menu-button-shade {
    background-color: map.get(colors.$colors, 'shamrock');
    border-radius: 100px;
    filter: blur(10px);
    height: 42px;
    left: 24px;
    opacity: 0.4;
    position: absolute;
    top: 10px;
    width: 41px;
  }

  .side-menu-entry-title {
    flex-grow: 1;
    font-size: 14px;
    line-height: 18px;
  }

  .side-menu-entry-title.collapsed {
    color: map.get(colors.$colors, 'biscay');
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
}

.side-menu-list-item-subtitle,
.side-menu-list-item {
  align-items: center;
  background-color: map.get(colors.$colors, 'alabaster');
  display: flex;
  height: 36px;
  padding: 0 16px 0 56px;
}

.side-menu-list-item-subtitle {
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}

.side-menu-list-item {
  cursor: pointer;
  flex-direction: row;
  gap: 10px;

  .side-menu-list-item-marker {
    background-color: map.get(colors.$colors, 'white-ice');
    height: 100%;
    width: 2px;
  }

  .side-menu-list-item-title {
    color: map.get(colors.$colors, 'stratos');
    font-size: 14px;
    line-height: 18px;
  }

  &.disabled .side-menu-list-item-title {
    color: map.get(colors.$colors, 'silver');
    pointer-events: none;
  }

  &.selected {
    .side-menu-list-item-marker {
      background-color: map.get(colors.$colors, 'shamrock');
    }
  }

  &:hover {
    background-color: map.get(colors.$colors, 'white-ice');

    &:not(.selected) .side-menu-list-item-marker {
      background-color: map.get(colors.$colors, 'shamrock');
    }
  }

  &:active {
    background-color: map.get(colors.$colors, 'shamrock');
  }

  &:focus:not(:active) {
    border: 2px solid map.get(colors.$colors, 'magic-mint');
    outline: 0;
    padding: 0 14px 0 54px;
  }
}

.group-divider-margined {
  margin-top: 10px;
}

.side-menu-footer {
  bottom: 30px;
  color: map.get(colors.$colors, 'dusty-gray');
  font-size: 12px;
  line-height: 16px;
  margin-left: 16px;
  position: absolute;
  width: 100%;
}
</style>
